<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">尾部广告管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.adv_name" placeholder="请输入广告名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增广告
        </el-button>
      </div>

      <!-- 广告列表 -->
      <el-table :data="adList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="adv_name" label="广告名称" width="180" />
        <el-table-column prop="adv_type" label="广告类型" width="120">
          <template #default="{ row }">
            <el-tag :type="getAdvTypeTag(row.adv_type)">
              {{ getAdvTypeText(row.adv_type) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="adv_url" label="外部链接" min-width="200" show-overflow-tooltip />
        <el-table-column prop="mini_name" label="小程序名称" width="150" />
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="200" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchAdList" @current-change="fetchAdList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="700px" :close-on-click-modal="false">
      <el-form ref="adForm" :model="adForm" label-width="120px" :rules="formRules">
        <el-form-item label="广告名称" prop="adv_name">
          <el-input v-model="adForm.adv_name" placeholder="请输入广告名称" size="large" />
        </el-form-item>

        <el-form-item label="广告类型" prop="adv_type">
          <el-radio-group v-model="adForm.adv_type" @change="handleAdvTypeChange">
            <el-radio :label="1">不挂载</el-radio>
            <el-radio :label="2">挂载外部链接</el-radio>
            <el-radio :label="3">挂载小程序</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 外部链接配置 -->
        <div v-if="adForm.adv_type === 2">
          <el-form-item label="外部链接" prop="adv_url">
            <el-input v-model="adForm.adv_url" placeholder="请输入外部链接地址, 例: https://www.baidu.com" size="large" />

            <!-- 外部链接提示 -->
            <div class="form-tip">
              外部链接必须认证的公众号才可挂载!
            </div>
          </el-form-item>
        </div>

        <!-- 小程序配置 -->
        <div v-if="adForm.adv_type === 3">
          <el-form-item label="小程序ID" prop="mini_appid">
            <el-input v-model="adForm.mini_appid" placeholder="请输入小程序APPID, 例: wxedeexxxxxxx" size="large" />
          </el-form-item>

          <el-form-item label="小程序路径" prop="mini_path">
            <el-input v-model="adForm.mini_path" placeholder="请输入小程序路径, 例: /pages/index/index" size="large" />
          </el-form-item>

          <el-form-item label="小程序名称" prop="mini_name">
            <el-input v-model="adForm.mini_name" placeholder="请输入小程序名称, 例: 某某某" size="large" />
          </el-form-item>
        </div>

        <!-- 使用RichTextEditor代替原来的富文本编辑器 -->
        <el-form-item label="广告内容" prop="adv_content">
          <RichTextEditor ref="richTextEditor" v-model:content="adForm.adv_content" @error="handleEditorError"
            @show-html="showHtmlDialog" />
        </el-form-item>


        <el-form-item label="备注信息">
          <el-input v-model="adForm.notes" placeholder="请输入备注信息" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAdForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete
} from '@element-plus/icons-vue'
import RichTextEditor from '@/components/RichTextEditor.vue';

export default {
  name: 'MaterialFooterAd',
  components: { Search, Plus, Edit, Delete, RichTextEditor },
  data() {
    return {
      loading: false,
      dialogVisible: false,
      dialogTitle: '新增广告',
      isEditMode: false,
      searchForm: {
        adv_name: ''
      },
      adForm: {
        id: null,
        adv_type: 1,
        adv_name: '',
        adv_url: '',
        mini_appid: '',
        mini_path: '',
        mini_name: '',
        adv_content: '',
        notes: ''
      },
      formRules: {
        adv_name: [
          { required: true, message: '请输入广告名称', trigger: 'blur' }
        ],
        adv_type: [
          { required: true, message: '请选择广告类型', trigger: 'change' }
        ],
        adv_url: [
          { required: true, message: '请输入外部链接', trigger: 'blur' }
        ],
        mini_appid: [
          { required: true, message: '请输入小程序ID', trigger: 'blur' }
        ],
        mini_path: [
          { required: true, message: '请输入小程序路径', trigger: 'blur' }
        ],
        mini_name: [
          { required: true, message: '请输入小程序名称', trigger: 'blur' }
        ],
        adv_content: [
          { required: true, message: '请输入广告内容', trigger: 'blur' }
        ]
      },
      adList: [],
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchAdList();
  },
  methods: {
    // 获取广告类型标签
    getAdvTypeTag(type) {
      switch (type) {
        case 1: return 'info';
        case 2: return 'primary';
        case 3: return 'success';
        default: return '';
      }
    },

    // 获取广告类型文本
    getAdvTypeText(type) {
      switch (type) {
        case 1: return '不挂载';
        case 2: return '外部链接';
        case 3: return '小程序';
        default: return '未知';
      }
    },

    // 处理广告类型变化
    handleAdvTypeChange() {
      // 重置相关字段
      this.adForm.adv_url = '';
      this.adForm.mini_appid = '';
      this.adForm.mini_path = '';
      this.adForm.mini_name = '';
    },
    // 显示HTML代码弹窗
    showHtmlDialog(html) {
      this.$prompt('HTML代码', '查看HTML源码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        inputType: 'textarea',
        inputValue: html,
        customClass: 'html-code-dialog',
        inputAttributes: {
          rows: 10,
          style: 'width: 100%; font-family: monospace;'
        }
      }).then(() => { }).catch(() => { });
    },

    // 处理富文本编辑器错误
    handleEditorError(message) {
      this.$message.error(message);
    },


    // 获取广告列表
    async fetchAdList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.adv_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        // 如果有搜索条件，加入搜索参数
        if (this.searchForm.adv_name) {
          params.adv_name = this.searchForm.adv_name;
        }

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/tail_advertisement.php`,
          params
        );

        if (data.data) {
          this.adList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取广告列表失败');
        console.error('获取广告列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchAdList();
    },

    // 打开新增对话框 - 修复内容清空问题
    openAddDialog() {
      this.dialogTitle = '新增广告';
      this.isEditMode = false;
      this.adForm = {
        id: null,
        adv_type: 1,
        adv_name: '',
        adv_url: '',
        mini_appid: '',
        mini_path: '',
        mini_name: '',
        adv_content: '', // 确保设置为空字符串
        notes: ''
      };

      // 添加关键代码：延迟确保编辑器内容重置
      setTimeout(() => {
        // 显式触发内容更新
        if (this.$refs.richTextEditor) {
          this.$refs.richTextEditor.clearContent(); // 调用富文本编辑器组件的清空方法
        }
      }, 100);

      this.dialogVisible = true;
    },

    // 打开编辑对话框 - 添加清除并重置的逻辑
    openEditDialog(row) {
      this.dialogTitle = '编辑广告';
      this.isEditMode = true;
      this.adForm = {
        id: row.id,
        adv_type: row.adv_type,
        adv_name: row.adv_name,
        adv_url: row.adv_url || '',
        mini_appid: row.mini_appid || '',
        mini_path: row.mini_path || '',
        mini_name: row.mini_name || '',
        adv_content: row.adv_content || '', // 确保内容字段设置正确
        notes: row.notes || ''
      };

      // 添加关键代码
      setTimeout(() => {
        if (this.$refs.richTextEditor) {
          this.$refs.richTextEditor.setContent(row.adv_content);
        }
      }, 100);

      this.dialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitAdForm() {
      this.$refs.adForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.adForm,
              store_id: this.userInfo.id
            };

            // 根据广告类型清理不需要的字段
            if (payload.adv_type === 1) {
              payload.adv_url = '';
              payload.mini_appid = '';
              payload.mini_path = '';
              payload.mini_name = '';
            } else if (payload.adv_type === 2) {
              payload.mini_appid = '';
              payload.mini_path = '';
              payload.mini_name = '';
            } else if (payload.adv_type === 3) {
              payload.adv_url = '';
            }

            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/tail_advertisement.php`,
              {
                action,
                ...payload
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchAdList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 删除广告
    async handleDelete(id) {
      this.$confirm('确定要删除该广告配置吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/tail_advertisement.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchAdList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/page-common.scss' as *;



.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

/* 统一提示样式 */
.form-tip {
  font-size: 12px;
  color: #dc290a;
  margin-top: 6px;
  line-height: 1.5;
}
</style>